import type { Option } from "@app/core";

import type { McpCostStatus, McpToolCallStatus, StatusCopy } from "./types";

/**
 * `rejected` and `error` are deliberately not merged. A rejection is a guardrail or permission
 * refusal that spent nothing; an error may still have issued queries that cost money, so folding
 * the two together would depress every cost-per-call figure a reader derives from this screen.
 */
export const STATUS_COPY: Record<McpToolCallStatus, StatusCopy> = {
  ok: {
    label: "OK",
    color: "success",
    hint: "Completed normally.",
  },
  rejected: {
    label: "Rejected",
    color: "warning",
    hint: "A guardrail or permission refusal. It reached no warehouse, so it spent no credits.",
  },
  error: {
    label: "Error",
    color: "error",
    hint: "An internal failure. Unlike a rejection, queries already issued still cost money — "
      + "check the credits column before assuming an error was free.",
  },
};

export const COST_STATUS_COPY: Record<McpCostStatus, StatusCopy> = {
  resolved: {
    label: "Resolved",
    color: "success",
    hint: "Snowflake has published the credits for every query this call issued.",
  },
  pending: {
    label: "Pending",
    color: "warning",
    hint: "Credits are not resolved yet. Snowflake publishes ACCOUNT_USAGE hours after the "
      + "fact, so cost reads as pending rather than zero — the query count above is already final.",
  },
  none: {
    label: "No queries",
    color: "neutral",
    hint: "The call issued no Snowflake queries at all, so there is nothing to attribute.",
  },
};

/**
 * VTabs mirrors the active tab into the URL as `#tab-<id>`, so these ids are what a link to a tab
 * looks like — and what the retired `/mcp-usage/calls` route redirects onto. Changing one breaks
 * every bookmark that was ever shared: treat them as part of the route, not as labels.
 */
export const MCP_TABS = {
  summary: "summary",
  calls: "calls",
} as const satisfies Record<string, string>;

export type McpTabId = typeof MCP_TABS[keyof typeof MCP_TABS];

/**
 * The one filter on this screen that survives a reload, kept in the URL query.
 *
 * It earns that over the tool and the status because it is the one a click can set: the Summary
 * breakdown drills into it, and a drill-down that a refresh throws away is a drill-down the reader
 * has to redo. Absent from `PERSISTENT_FILTER_PARAMS`, so it does not follow anyone off the page.
 */
export const USER_QUERY_PARAM = "userId";

/**
 * The height of a table inside a tab panel, for each shape the cost notice above the tabs can
 * take, keyed by how many lines that notice carries. `0` is no notice at all.
 *
 * One map for both tabs, not one each: the notice, the KPI cards and the tab strip sit above both
 * panels identically, so the space left underneath is the same on either. Two maps would be two
 * places to keep in step for a number that cannot legitimately differ.
 *
 * Complete values rather than one base minus per-state offsets: these are tuned by eye, and
 * arithmetic over a shared base means correcting one of them silently moves the other two. Folded
 * into each is the tab strip — a 50px bar (14px padding either side of a 20px line, plus the 2px
 * active border) and the panel's own 16px `padding-top`.
 *
 * They are `vh` figures rather than a flex fill because the app shell has no definite height —
 * `DefaultLayout` sizes its content column with `min-h-[calc(100vh-2rem)]`, so a percentage or
 * `flex: 1` height here resolves against an auto-height ancestor and collapses.
 *
 * Desktop figures. Below roughly 900px the notice text wraps further and the table gives back a
 * little less than it could — it under-fills rather than overflowing, so the miss is invisible.
 */
export const TAB_TABLE_HEIGHT: Readonly<Record<number, string>> = {
  0: "calc(65vh - 86px)",
  1: "calc(55vh - 94px)",
  2: "calc(55vh - 116px)",
};

/**
 * Used when the notice grows a shape the map has not been tuned for. The shortest of the three on
 * purpose: too short leaves a gap, too tall pushes the pager off the screen.
 */
export const TAB_TABLE_FALLBACK_HEIGHT = TAB_TABLE_HEIGHT[2];

/**
 * "No filter" as an explicit choice. Clearing a select by pressing its X is a hidden affordance;
 * an option the user can point at is not.
 */
export const ALL_VALUE = "";

/** Kept as a separate name at each call site so a reader sees which filter it clears. */
export const ALL_STATUSES_VALUE = ALL_VALUE;
export const ALL_TOOLS_VALUE = ALL_VALUE;
export const ALL_USERS_VALUE = ALL_VALUE;

export const STATUS_OPTIONS: ReadonlyArray<Option> = [
  { label: "All statuses", value: ALL_STATUSES_VALUE },
  ...Object.entries(STATUS_COPY).map(([value, copy]) => ({ label: copy.label, value })),
];

/**
 * What the total does and does not measure. It sits on the cost card because a reader who takes
 * it for the Snowflake bill will be wrong by more than a rounding error.
 */
export const COST_SCOPE_HINT
  = "Compute attributed to individual queries. It excludes warehouse idle time, cloud services, "
    + "storage and serverless, and each figure is a fair share of a warehouse running several "
    + "queries at once — so this is always lower than the Snowflake invoice, and is not a "
    + "reconciliation of it.";

/**
 * What the estimated figure beside the attributed total is, and why it does not agree with it.
 *
 * It sits on the estimate itself rather than the total, because the total already carries
 * `COST_SCOPE_HINT` and a reader comparing the two numbers is asking about the gap, not about
 * either figure's own scope.
 */
export const ESTIMATED_COST_HINT
  = "Derived from measured execution time at the configured warehouse rate, so it is available "
    + "immediately and does not wait on Snowflake's own attribution the way the total above does. "
    + "It is an approximation, not a second measurement: an over-estimate under load, since "
    + "concurrent queries share one warehouse, and it excludes idle time, cloud services and "
    + "storage the same way the attributed total does.";

/**
 * Why the screen shows credits but no dollars.
 *
 * It is a warning rather than a tooltip because the cost card is the one number this screen
 * exists to report, and an em dash where a figure belongs reads as "free" to anyone who does not
 * hover.
 */
export const UNPRICED_WARNING
  = "No credit price is configured, so dollars cannot be derived. The attributed and estimated "
    + "credits beside the total are both real — only their conversion into money is missing.";

/**
 * The two headings the notice above the KPI row can carry, and they are not interchangeable.
 *
 * Pending attribution is the steady state: the default window ends today and Snowflake publishes
 * about eight hours after a call, so on any working day something is pending. That is a status,
 * and it says so — nobody is expected to act on it.
 *
 * A missing credit price is a configuration an admin has to go and fix, and it is the only one of
 * the two that takes the warning tone. Colouring both the same would put an amber bar on screen
 * every day and spend the attention this one needs.
 */
export const COST_SETTLING_TITLE = "Cost is still settling";
export const COST_UNPRICED_TITLE = "Cost cannot be shown in dollars";

/**
 * How long after queueing a pass before the screen refetches.
 *
 * The pass runs in a background worker, so there is nothing to wait on from here — this is a
 * guess at how long the queue takes to pick the job up, not a guarantee the credits have landed.
 * The reader can press again once the cooldown clears if the figures have not moved.
 */
export const CREDIT_REFRESH_REFETCH_DELAY_MS = 3000;

/**
 * The caveat that keeps the button from reading as broken.
 *
 * Snowflake publishes ACCOUNT_USAGE hours after the fact, so pressing this cannot resolve
 * anything from today — what it buys is the wait between the four-hourly runs, not the wait on
 * Snowflake. A reader who does not know that will press it, see the pending count unchanged,
 * and conclude the feature does not work.
 */
export const CREDIT_REFRESH_HINT
  = "Runs the Snowflake credit attribution pass now instead of waiting for the four-hourly one. "
    + "Snowflake publishes attribution about eight hours after a call, so very recent calls stay "
    + "pending however often this is pressed.";
